/**
 * Traduction du plan de pagination en pages affichables.
 *
 * Deux constantes gouvernent l'affichage et vivent ici :
 * un graphe porte au plus 26 colonnes (découpage de devideSerieSecteur dans
 * createSeries.tsx), et une page affiche 8 graphes.
 */
export const BOXPLOTS_PAR_GRAPHE = 26;
export const GRAPHES_PAR_PAGE = 8;

export type PlanGroupe = {
    secteur: number | null;
    famille: number | null;
    nutriment: number;
    /** Nombre de colonnes du groupe, au sens des clés de dataSeries. */
    boxplots: number;
};

/**
 * Réponse brute de produits/stats/diagram/plan : une ligne
 * [secteur, famille, nutriment, indice d'année, segment] au grain de la requête
 * de données, plus les libellés en dictionnaires.
 */
export type PlanBrut = {
    lignes: number[][];
    secteurs: HashMap<string>;
    familles: HashMap<string>;
    nutriments: HashMap<{ nom: string; score: number }>;
    annees: string[];
};

/**
 * Clé de colonne, reprise à l'identique de createSeries.tsx.
 *
 * C'est elle que devideSerieSecteur compte pour découper les graphes, et non le
 * nombre de lignes : en cas C et E elle porte le *libellé* de famille, si bien
 * que deux secteurs partageant un libellé ne forment qu'une colonne.
 *
 * - secteur × nutriment (cas A et B) : famille + année      (createSeries.tsx:421)
 * - famille × nutriment (cas D)      : marque + année       (createSeries.tsx:733)
 * - nutriment seul (cas C et E)      : libellé + marque + année (createSeries.tsx:579, 879)
 */
const cleDeColonne = (
    groupBy: string,
    ligne: number[],
    familles: HashMap<string>
): string => {
    const [, famille, , annee, segment] = ligne;

    if (groupBy === "secteur_nutriment") {
        return `${famille}-${annee}`;
    }

    if (groupBy === "famille_nutriment") {
        return `${segment}-${annee}`;
    }

    return `${familles[famille] || famille}-${segment}-${annee}`;
};

/**
 * Regroupe les lignes du plan selon le cas de graphe affiché, dans l'ordre
 * d'affichage.
 *
 * Le tri appartient au client, pas au SQL : aucune collation de la base ne
 * reproduit localeCompare — utf8mb4_general_ci classe l'underscore après les
 * lettres et ne développe pas la ligature « œ », l'algorithme unicode fait
 * l'inverse. Or c'est bien localeCompare qui ordonnait les graphes avant la
 * pagination, dans sortByPriorityNutriment.
 *
 * L'ordre reproduit celui-ci exactement : les lignes sont triées par secteur,
 * famille, priorité du nutriment puis libellé, et un groupe prend le rang de sa
 * première ligne — comme les clés de dataSeries se créaient au fil des lignes.
 */
export const construirePlan = (brut: PlanBrut, groupBy: string): PlanGroupe[] => {
    if (!brut?.lignes?.length) {
        return [];
    }

    const { secteurs, familles, nutriments } = brut;
    const nom = (n?: string) => n || "";

    // Rangs pré-calculés : localeCompare est coûteux, et la sélection la plus
    // large porte près de 19 000 lignes.
    const rangs = (noms: string[]): HashMap<number> => {
        const table: HashMap<number> = {};
        [...new Set(noms)].sort((a, b) => a.localeCompare(b)).forEach((n, i) => {
            table[n] = i;
        });
        return table;
    };
    const rangSecteur = rangs(Object.values(secteurs).map(nom));
    const rangFamille = rangs(Object.values(familles).map(nom));
    const rangNutriment = rangs(Object.values(nutriments).map((n) => nom(n?.nom)));

    const cleSecteur = (l: number[]) => rangSecteur[nom(secteurs[l[0]])] ?? 0;
    const cleFamille = (l: number[]) => rangFamille[nom(familles[l[1]])] ?? 0;
    const score = (l: number[]) => nutriments[l[2]]?.score ?? 99;
    const cleNutriment = (l: number[]) => rangNutriment[nom(nutriments[l[2]]?.nom)] ?? 0;

    const lignes = [...brut.lignes].sort(
        (a, b) =>
            cleSecteur(a) - cleSecteur(b) ||
            cleFamille(a) - cleFamille(b) ||
            score(a) - score(b) ||
            cleNutriment(a) - cleNutriment(b)
    );

    const groupes = new Map<string, PlanGroupe>();
    const colonnes = new Map<string, Set<string>>();

    lignes.forEach((ligne) => {
        const groupe: PlanGroupe = {
            secteur: groupBy === "secteur_nutriment" ? ligne[0] : null,
            famille: groupBy === "famille_nutriment" ? ligne[1] : null,
            nutriment: ligne[2],
            boxplots: 0,
        };
        const cle = cleDeGroupe(groupe);

        if (!groupes.has(cle)) {
            groupes.set(cle, groupe);
            colonnes.set(cle, new Set());
        }

        colonnes.get(cle)!.add(cleDeColonne(groupBy, ligne, familles));
    });

    groupes.forEach((groupe, cle) => {
        groupe.boxplots = colonnes.get(cle)!.size;
    });

    return [...groupes.values()];
};

export const chartsOfGroupe = (groupe: PlanGroupe): number =>
    Math.max(1, Math.ceil(groupe.boxplots / BOXPLOTS_PAR_GRAPHE));

export const totalCharts = (plan: PlanGroupe[]): number =>
    plan.reduce((total, groupe) => total + chartsOfGroupe(groupe), 0);

export const lastPage = (plan: PlanGroupe[]): number =>
    Math.max(1, Math.ceil(totalCharts(plan) / GRAPHES_PAR_PAGE));

export const cleDeGroupe = (groupe: { secteur: number | null; famille: number | null; nutriment: number }): string =>
    `${groupe.secteur ?? ""}|${groupe.famille ?? ""}|${groupe.nutriment}`;

/**
 * Groupes couvrant les 8 graphes de la page demandée, et position du premier
 * graphe de la page dans le premier groupe retourné.
 *
 * Les groupes sont retournés entiers : l'échelle Y et la légende d'unités d'un
 * graphe se calculent sur le groupe complet, un groupe tronqué serait faux. Les
 * graphes chargés en trop sont ceux de la page suivante, gardés en cache.
 */
export const groupesDeLaPage = (
    plan: PlanGroupe[],
    page: number
): { groupes: PlanGroupe[]; premierGraphe: number } => {
    const debut = (page - 1) * GRAPHES_PAR_PAGE;
    const fin = debut + GRAPHES_PAR_PAGE;

    const groupes: PlanGroupe[] = [];
    let premierGraphe = 0;
    let position = 0;

    for (const groupe of plan) {
        const taille = chartsOfGroupe(groupe);
        const chevauche = position < fin && position + taille > debut;

        if (chevauche) {
            if (!groupes.length) {
                premierGraphe = debut - position;
            }
            groupes.push(groupe);
        }

        position += taille;

        if (position >= fin) {
            break;
        }
    }

    return { groupes, premierGraphe };
};
